'use client';
import React, { useState, useEffect } from 'react';
import { FilterState } from './DiscoverScreen';
import { SlidersHorizontal, RotateCcw, Search } from 'lucide-react';
import { US_STATES, KENYAN_COUNTIES, RELATIONSHIP_INTENTIONS } from '@/lib/data';

interface DiscoverFiltersProps {
  filters: FilterState;
  onFilterChange: (f: FilterState) => void;
  totalResults: number;
  onSearch?: () => void;
}

const labelClass = 'text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block';
const selectClass = 'input-jsn text-sm py-2 appearance-none cursor-pointer';

export default function DiscoverFilters({ filters, onFilterChange, totalResults, onSearch }: DiscoverFiltersProps) {
  const [pending, setPending] = useState<FilterState>(filters);

  // Sync pending when external filters reset
  useEffect(() => {
    setPending(filters);
  }, [filters]);

  const update = (key: keyof FilterState, value: string | number) => {
    setPending(prev => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    const cleared: FilterState = { ageMin: 21, ageMax: 70, gender: '', intention: '', usState: '', kenyanCounty: '', faith: '' };
    setPending(cleared);
    onFilterChange(cleared);
  };

  const handleSearch = () => {
    onFilterChange(pending);
    onSearch?.();
  };

  const hasActiveFilters = filters.gender || filters.intention || filters.usState || filters.kenyanCounty || filters.faith || filters.ageMin !== 21 || filters.ageMax !== 70;

  return (
    <div className="p-5 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} className="text-primary" />
          <span className="font-semibold text-foreground text-sm">Filters</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{totalResults} results</span>
        </div>
      </div>

      {/* Age Range */}
      <div>
        <label className={labelClass}>Age Range</label>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground font-medium">{pending.ageMin}</span>
            <span className="text-muted-foreground text-xs">to</span>
            <span className="text-foreground font-medium">{pending.ageMax}</span>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Min age: {pending.ageMin}</p>
              <input
                type="range"
                min={21}
                max={pending.ageMax - 1}
                value={pending.ageMin}
                onChange={(e) => update('ageMin', Number(e.target.value))}
                className="w-full accent-primary cursor-pointer"
              />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Max age: {pending.ageMax}</p>
              <input
                type="range"
                min={pending.ageMin + 1}
                max={70}
                value={pending.ageMax}
                onChange={(e) => update('ageMax', Number(e.target.value))}
                className="w-full accent-primary cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="divider-red" />

      {/* Gender */}
      <div>
        <label className={labelClass}>Gender</label>
        <select
          value={pending.gender}
          onChange={(e) => update('gender', e.target.value)}
          className={selectClass}
        >
          <option value="">All genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </div>

      {/* Relationship Intention */}
      <div>
        <label className={labelClass}>Relationship Intention</label>
        <select
          value={pending.intention}
          onChange={(e) => update('intention', e.target.value)}
          className={selectClass}
        >
          <option value="">All intentions</option>
          {RELATIONSHIP_INTENTIONS.filter(i => i !== 'Courtship').map(i => (
            <option key={`filter-intent-${i}`} value={i}>{i}</option>
          ))}
        </select>
      </div>

      <div className="divider-red" />

      {/* US State */}
      <div>
        <label className={labelClass}>U.S. State</label>
        <select
          value={pending.usState}
          onChange={(e) => update('usState', e.target.value)}
          className={selectClass}
        >
          <option value="">All states</option>
          {US_STATES.map(s => (
            <option key={`filter-state-${s}`} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Kenyan County */}
      <div>
        <label className={labelClass}>Kenyan County / Background</label>
        <select
          value={pending.kenyanCounty}
          onChange={(e) => update('kenyanCounty', e.target.value)}
          className={selectClass}
        >
          <option value="">All counties</option>
          {KENYAN_COUNTIES.map(c => (
            <option key={`filter-county-${c}`} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Faith */}
      <div>
        <label className={labelClass}>Faith / Values</label>
        <select
          value={pending.faith}
          onChange={(e) => update('faith', e.target.value)}
          className={selectClass}
        >
          <option value="">All faiths</option>
          {['Christian', 'Muslim', 'Hindu', 'Traditional', 'Spiritual'].map(f => (
            <option key={`filter-faith-${f}`} value={f}>{f}</option>
          ))}
        </select>
      </div>

      {/* Search Button */}
      <button
        onClick={handleSearch}
        className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
        style={{ backgroundColor: '#008000', color: '#ffffff' }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#006600')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#008000')}
      >
        <Search size={15} />
        Search
      </button>

      {/* Reset Filters Button — always visible below Search */}
      <button
        onClick={reset}
        className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
        style={{ background: 'transparent', color: '#750000', border: '1px solid #750000' }}
        onMouseEnter={e => { (e.currentTarget.style.backgroundColor = '#750000'); (e.currentTarget.style.color = '#ffffff'); }}
        onMouseLeave={e => { (e.currentTarget.style.backgroundColor = 'transparent'); (e.currentTarget.style.color = '#750000'); }}
      >
        <RotateCcw size={14} />
        Reset Filters
      </button>
    </div>
  );
}